import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { usePerformSettingAction } from 'settings/queries/perform_action'
import { useUserLocaleSetting } from 'settings/queries/settings'
import { SingleSelectSetting } from 'settings/types'
import { getSettingsAction } from 'settings/utils'
import i18n from './i18l'

export function useServerLanguageSync(enabled: boolean) {
  const localeSettingQuery = useUserLocaleSetting(enabled)
  const settingMutation = usePerformSettingAction({ type: 'user' })
  const queryClient = useQueryClient()
  const [isUpdating, setUpdating] = useState(false)

  if (isUpdating) {
    return
  }

  if (!enabled) {
    return
  }

  const localeSetting = localeSettingQuery.data

  if (!localeSetting) {
    return
  }

  const serverLocale = localeSetting.selectedId
  const deviceLocale = i18n.locale

  if (serverLocale.length === 0 || deviceLocale.length === 0) {
    return
  }

  const serverLanguage = getLanguageFromLocale(serverLocale)
  const deviceLanguage = getLanguageFromLocale(deviceLocale)

  const localeSettingValue = findLocaleSettingValue(
    localeSetting,
    deviceLanguage
  )

  if (serverLanguage !== deviceLanguage && localeSettingValue) {
    updateServerLanguage().catch(console.warn)
  }

  async function updateServerLanguage() {
    setUpdating(true)

    try {
      if (localeSetting && localeSettingValue) {
        const settingsAction = getSettingsAction(localeSetting, [
          localeSettingValue.id,
        ])
        await settingMutation.mutateAsync(settingsAction)
        await resetAllQueries(queryClient)
      }
    } finally {
      setUpdating(false)
    }
  }
}

function findLocaleSettingValue(
  localeSetting: SingleSelectSetting,
  language: string
) {
  return localeSetting.values.find(value => {
    return getLanguageFromLocale(value.id) === language
  })
}

function getLanguageFromLocale(locale: string) {
  return locale.split('-')[0]
}

async function resetAllQueries(queryClient: QueryClient) {
  queryClient.clear()
  await queryClient.invalidateQueries()
  await queryClient.refetchQueries()
}
